/**
 * Token/storage platform adapter (R1.2a / R1.2a-FIX).
 *
 * Used for access token and refresh token only.
 *
 * - NATIVE (iOS / Android):
 *   Uses capacitor-secure-storage-plugin → Keychain (iOS) / Keystore (Android).
 *   Values are encrypted at OS level.
 *
 * - WEB:
 *   Uses sessionStorage (not localStorage). Tokens are cleared when the tab/window
 *   is closed. No additional encryption: sessionStorage is not encrypted.
 *   True mitigation for production web would be httpOnly cookies set by the backend
 *   (requires backend change); see docs/releases/R1.2a/Criticità.md and future_improvements.
 */

import { isNative } from './isNative';
import { SecureStoragePlugin } from 'capacitor-secure-storage-plugin';

function webStorage(): Storage {
  return typeof window !== 'undefined' ? window.sessionStorage : ({} as Storage);
}

export const SecureStorage = {
  async get(key: string): Promise<string | null> {
    if (isNative()) {
      try {
        const { value } = await SecureStoragePlugin.get({ key });
        return value;
      } catch {
        return null;
      }
    }
    return webStorage().getItem(key);
  },

  async set(key: string, value: string): Promise<void> {
    if (isNative()) {
      await SecureStoragePlugin.set({ key, value });
      return;
    }
    webStorage().setItem(key, value);
  },

  async remove(key: string): Promise<void> {
    if (isNative()) {
      try {
        await SecureStoragePlugin.remove({ key });
      } catch {
        // key may not exist
      }
      return;
    }
    webStorage().removeItem(key);
  },
};
